import { CheckCircle2, XCircle } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toApiError } from '@/api/errors'
import { toast } from '@/components/feedback/toast-store'
import { FormField } from '@/components/forms/FormField'
import { Button } from '@/components/ui/Button'
import { Dialog } from '@/components/ui/Dialog'
import { Textarea } from '@/components/ui/Textarea'
import { useDecideLeave } from '../api'
import { formatDays, formatRange, type LeaveRequest } from '../types'

interface Props {
  request: LeaveRequest
  decision: 'approve' | 'reject'
  onOpenChange: (open: boolean) => void
  onDone?: (updated: LeaveRequest) => void
}

/** Approve (optional note) or reject (note required) a pending request. Mounted only while open. */
export function DecisionDialog({ request, decision, onOpenChange, onDone }: Props) {
  const { t } = useTranslation('leave')
  const decide = useDecideLeave()
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | undefined>()
  const approving = decision === 'approve'

  const submit = () => {
    if (!approving && !note.trim()) {
      setError(t('Explain why the request is rejected.'))
      return
    }
    decide.mutate(
      { id: request.id, decision, note: note.trim() || null },
      {
        onSuccess: (updated) => {
          toast.success(
            approving ? t('Leave approved') : t('Leave rejected'),
            `${request.person.name} · ${formatRange(request)}`,
          )
          onDone?.(updated)
          onOpenChange(false)
        },
        onError: (e) => {
          const err = toApiError(e)
          if (err.violations[0]) setError(err.violations[0].message)
          else toast.error(approving ? t('Could not approve') : t('Could not reject'), err.message)
        },
      },
    )
  }

  return (
    <Dialog
      open
      onOpenChange={onOpenChange}
      size="sm"
      dismissible={!decide.isPending}
      icon={
        approving ? <CheckCircle2 className="text-success-600" /> : <XCircle className="text-danger-600" />
      }
      title={approving ? t('Approve leave?') : t('Reject leave?')}
      description={`${request.person.name} · ${request.type.name} · ${formatRange(request)} (${formatDays(request.days)})`}
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={decide.isPending}>
            {t('Cancel')}
          </Button>
          <Button variant={approving ? 'primary' : 'danger'} loading={decide.isPending} onClick={submit}>
            {approving ? t('Approve') : t('Reject')}
          </Button>
        </>
      }
    >
      <FormField
        label={approving ? t('Note (optional)') : t('Reason for rejecting')}
        required={!approving}
        error={error}
        hint={t('Shared with the requester.')}
      >
        <Textarea
          rows={3}
          maxLength={500}
          value={note}
          onChange={(e) => {
            setNote(e.target.value)
            setError(undefined)
          }}
          autoFocus
        />
      </FormField>
    </Dialog>
  )
}
